/**
 * React hook for uploading audio files to the API
 *
 * Wraps the API client's uploadAudio method with TanStack Query mutation
 * for automatic state management, caching, and error handling.
 */

import { useMutation } from "@tanstack/react-query";
import { createApiClient, ApiClientError } from "@floline/api-client";
import type { AudioUploadResponse } from "@floline/api-client/types";
import { API_URL } from "@/env";
import { i18n } from "@/i18n";

// ============================================================================
// API Client
// ============================================================================

/** API client configured for audio upload requests */
const apiClient = createApiClient(
  API_URL
);

// ============================================================================
// Types
// ============================================================================

export interface AudioUploadInput {
  /** Audio blob to upload */
  blob: Blob;
  /** Optional filename (defaults to recording.webm) */
  filename?: string;
}

export interface UseAudioUploadOptions {
  /** Callback when upload succeeds */
  onSuccess?: (result: AudioUploadResponse) => void;
  /** Callback when upload fails */
  onError?: (error: Error) => void;
}

export interface UseAudioUploadReturn {
  /** Upload the audio file */
  uploadAudio: (input: AudioUploadInput) => Promise<AudioUploadResponse>;
  /** Whether upload is in progress */
  isUploading: boolean;
  /** Upload progress state */
  uploadState: "idle" | "uploading" | "success" | "error";
  /** Upload error if any */
  error: Error | null;
  /** Reset the upload state */
  reset: () => void;
}

// ============================================================================
// Upload Function
// ============================================================================

/**
 * Upload audio blob to the API
 *
 * @param input - Audio blob and optional filename
 * @returns Upload result with blob URL and metadata
 */
async function uploadAudioFile(
  input: AudioUploadInput
): Promise<AudioUploadResponse> {
  try {
    return await apiClient.uploadAudio(input.blob, input.filename);
  } catch (error: unknown) {
    // Rethrow with more descriptive message for common errors
    if (error instanceof ApiClientError) {
      if (error.status === 401) {
        throw new Error(i18n.t("errors.signInToUploadAudio"));
      }
      if (error.status === 413 || error.code === "FILE_TOO_LARGE") {
        throw new Error(i18n.t("errors.audioUploadTooLarge"));
      }
      if (error.code === "INVALID_MIME_TYPE") {
        throw new Error(i18n.t("errors.unsupportedAudioUploadFormat"));
      }
    }
    throw error;
  }
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for uploading audio recordings to blob storage
 *
 * @example
 * ```tsx
 * const { uploadAudio, isUploading, error } = useAudioUpload({
 *   onSuccess: (result) => {
 *     console.log('Uploaded:', result.blobUrl);
 *   },
 *   onError: (error) => {
 *     console.error('Upload failed:', error);
 *   },
 * });
 *
 * // Upload a recording
 * const result = await uploadAudio({ blob: audioBlob, filename: 'my-recording.webm' });
 * ```
 */
export function useAudioUpload(
  options: UseAudioUploadOptions = {}
): UseAudioUploadReturn {
  const { onSuccess, onError } = options;

  const mutation = useMutation({
    mutationFn: uploadAudioFile,
    onSuccess,
    onError,
  });

  // Derive upload state from mutation status
  const uploadState = mutation.isPending
    ? "uploading"
    : mutation.isSuccess
      ? "success"
      : mutation.isError
        ? "error"
        : "idle";

  return {
    uploadAudio: mutation.mutateAsync,
    isUploading: mutation.isPending,
    uploadState,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ============================================================================
// Query Keys (for potential future use)
// ============================================================================

/** Query key factory for audio uploads */
export const audioUploadKeys = {
  all: ["audio-uploads"] as const,
} as const;
